import React, { useCallback, useRef } from "react";
import _ from "lodash";
import { useSelector } from "react-redux";
import { createSelector } from "reselect";

import {
  Button,
  ClickAwayListener,
  Grow,
  Link,
  MenuItem,
  MenuList,
  Paper,
  Popper
} from "@material-ui/core";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import { makeStyles } from "@material-ui/core/styles";

import { defaultUserName } from "utils/initialize";

const useStyles = makeStyles(theme => ({
  button: {
    fontSize: theme.typography.body1.fontSize,
    fontWeight: "normal",
    "& .MuiButton-label": {
      marginRight: 0,
      textTransform: "none"
    }
  },
  link: {
    width: "100%",
    padding: "6px 16px"
  },
  listItem: {
    padding: 0
  },
  popper: {
    zIndex: theme.zIndex.speedDial
  },
  loginLink: {
    marginRight: theme.spacing(1)
  }
}));

const selectUser = createSelector(state => state.user, _.identity);
const loginLink = `https://account.opengamingnetwork.com/login?returnurl=`;
const logoutLink = ` https://account.opengamingnetwork.com/login?logout=1&returnurl=`;

const UserMenu = ({ anchorRef }) => {
  const classes = useStyles();
  const selfRef = useRef(null);
  const user = useSelector(selectUser);
  const { username } = user;

  const returnURL = encodeURIComponent(window.location.href);

  const [open, setOpen] = React.useState(false);
  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen);
  };

  const refToUse = anchorRef ?? selfRef;
  const placement = anchorRef ? "bottom-end" : "bottom-start";

  const handleClose = useCallback(
    event => {
      if (refToUse?.current && refToUse?.current.contains(event.target)) {
        return;
      }

      setOpen(false);
    },
    [refToUse]
  );

  const handleListKeyDown = event => {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpen(false);
    }
  };

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = React.useRef(open);
  React.useEffect(() => {
    if (prevOpen.current === true && open === false) {
      // eslint-disable-next-line no-unused-expressions
      refToUse?.current.focus();
    }

    prevOpen.current = open;
  }, [open, refToUse]);

  if (username && username !== defaultUserName) {
    return (
      <>
        <Popper
          className={classes.popper}
          open={open}
          anchorEl={refToUse?.current}
          role="menu"
          transition
          disablePortal
          placement={placement}
        >
          {({ TransitionProps }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin: "center top"
              }}
            >
              <Paper>
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList
                    autoFocusItem={open}
                    id="OGN-user-list"
                    onKeyDown={handleListKeyDown}
                  >
                    <MenuItem
                      className={classes.listItem}
                      onKeyDown={event => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          setOpen(false);
                          window.location.href = `${logoutLink}${returnURL}`;
                        }
                      }}
                    >
                      <Link
                        className={classes.link}
                        onClick={handleClose}
                        target="_self"
                        href={`${logoutLink}${returnURL}`}
                        underline="none"
                        color="inherit"
                        variant="body1"
                      >
                        Log Out
                      </Link>
                    </MenuItem>
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
        <Button
          ref={selfRef}
          id="user-menu-button"
          className={classes.button}
          color="inherit"
          endIcon={<ArrowDropDownIcon />}
          onClick={handleToggle}
        >
          {username}
        </Button>
      </>
    );
  } else {
    return (
      <Link
        className={classes.loginLink}
        target="_self"
        href={`${loginLink}${returnURL}`}
        underline="none"
        color="inherit"
        variant="body1"
      >
        Login / Register
      </Link>
    );
  }
};

export default UserMenu;
